import { type LeaveCalendarDay, type LeaveCalendarEntry } from '@gb-ticket/shared-types';

import { cn } from '@/lib/utils';
import {
  leavePanelClassName,
  leaveTableFrameClassName,
} from '@/products/leave/features/leave-panel';
import { UserAvatar } from '@/platform/components/user-avatar';
import { LeaveDayCell } from '@/products/leave/features/leave-day-cell';
import {
  formatGermanDate,
  isoWeekNumber,
  schoolHolidayNameOn,
  WEEKDAYS,
  type SchoolHolidaySpan,
} from '@/products/leave/features/leave-calendar-utils';
import { leaveRequestDetailPath } from '@/products/leave/leave-paths';

export type ColleagueMatrixPerson = {
  id: string;
  firstName: string;
  lastName: string;
  displayName: string;
  departmentName?: string | null;
  profilePhotoUrl?: string | null;
};

type Props = {
  people: readonly ColleagueMatrixPerson[];
  days: readonly LeaveCalendarDay[];
  schoolHolidays?: readonly SchoolHolidaySpan[];
  today: string;
  viewerId: string;
  showDepartment?: boolean;
};

export function LeaveColleagueMatrix({
  people,
  days,
  schoolHolidays = [],
  today,
  viewerId,
  showDepartment = false,
}: Props) {
  const weeks = chunkWeeks(days);

  return (
    <>
      <div className={cn(leaveTableFrameClassName, 'p-3')}>
        <table className="w-max border-separate border-spacing-0 text-xs">
          <thead>
            <tr>
              <th className="sticky left-0 z-20 bg-card" />
              {weeks.map((week) => {
                const first = week[0];
                if (!first) {
                  return null;
                }
                return (
                  <th
                    key={first.date}
                    colSpan={week.length}
                    className="px-1 py-1 text-left font-medium text-muted-foreground"
                  >
                    KW {isoWeekNumber(first.date)}
                  </th>
                );
              })}
            </tr>
            <tr>
              <th className="sticky left-0 z-20 bg-card" />
              {days.map((day) => (
                <th
                  key={`${day.date}-weekday`}
                  className="px-0.5 py-0.5 text-center font-medium text-muted-foreground"
                >
                  {WEEKDAYS[(new Date(`${day.date}T00:00:00.000Z`).getUTCDay() + 6) % 7]}
                </th>
              ))}
            </tr>
            <tr>
              <th className="sticky left-0 z-20 min-w-44 bg-card px-2 text-left font-medium">
                Name
              </th>
              {days.map((day) => (
                <th
                  key={`${day.date}-number`}
                  className={cn(
                    'px-0.5 py-1 text-center font-semibold',
                    day.date === today ? 'text-primary' : null,
                  )}
                >
                  {Number(day.date.slice(8, 10))}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {people.map((person) => (
              <tr key={person.id}>
                <th
                  scope="row"
                  className="sticky left-0 z-10 bg-card px-2 py-1 text-left font-medium"
                >
                  <span className="flex items-center gap-2">
                    <span className="inline-flex size-7 shrink-0">
                      <UserAvatar
                        firstName={person.firstName}
                        lastName={person.lastName}
                        displayName={person.displayName}
                        profilePhotoUrl={person.profilePhotoUrl}
                      />
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate">{person.displayName}</span>
                      {showDepartment && person.departmentName ? (
                        <span className="block truncate font-normal text-muted-foreground">
                          {person.departmentName}
                        </span>
                      ) : null}
                    </span>
                  </span>
                </th>
                {days.map((day) => (
                  <td key={`${person.id}-${day.date}`} className="p-0.5">
                    <MatrixCell
                      day={day}
                      person={person}
                      entries={day.entries.filter((entry) => entry.userId === person.id)}
                      schoolHolidays={schoolHolidays}
                      today={today}
                      viewerId={viewerId}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ul className="space-y-4 lg:hidden">
        {people.map((person) => {
          const absences = days.flatMap((day) =>
            day.entries
              .filter((entry) => entry.userId === person.id)
              .map((entry) => ({ entry, date: day.date })),
          );
          return (
            <li key={`${person.id}-list`} className={leavePanelClassName}>
              <p className="font-medium">{person.displayName}</p>
              {showDepartment && person.departmentName ? (
                <p className="text-sm text-muted-foreground">{person.departmentName}</p>
              ) : null}
              {absences.length === 0 ? (
                <p className="mt-1 text-sm text-muted-foreground">
                  Keine Abwesenheit in diesem Zeitraum.
                </p>
              ) : (
                <ul className="mt-2 space-y-1">
                  {absences.map(({ entry, date }) => (
                    <li key={`${entry.requestId}-${date}`} className="flex items-center gap-2">
                      <AbsenceChip
                        entry={entry}
                        dayNumber={Number(date.slice(8, 10))}
                        href={
                          person.id === viewerId ? leaveRequestDetailPath(entry.requestId) : null
                        }
                      />
                      <span className="text-sm">{formatGermanDate(date)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </>
  );
}

function chunkWeeks(days: readonly LeaveCalendarDay[]): LeaveCalendarDay[][] {
  const weeks: LeaveCalendarDay[][] = [];
  for (let index = 0; index < days.length; index += 7) {
    weeks.push([...days.slice(index, index + 7)]);
  }
  return weeks;
}

function MatrixCell({
  day,
  person,
  entries,
  schoolHolidays,
  today,
  viewerId,
}: {
  day: LeaveCalendarDay;
  person: ColleagueMatrixPerson;
  entries: LeaveCalendarEntry[];
  schoolHolidays: readonly SchoolHolidaySpan[];
  today: string;
  viewerId: string;
}) {
  const weekday = new Date(`${day.date}T00:00:00.000Z`).getUTCDay();
  const weekend = weekday === 0 || weekday === 6;
  const schoolName = schoolHolidayNameOn(day.date, schoolHolidays);
  const holidayName = day.holidays[0]?.nameDe ?? null;
  if (entries.length === 0) {
    return (
      <div
        title={[formatGermanDate(day.date), holidayName, schoolName].filter(Boolean).join(' · ')}
        className={cn(
          'size-7 rounded-sm border',
          schoolName ? 'bg-orange-100 dark:bg-orange-950/50' : null,
          weekend && !schoolName ? 'bg-muted' : null,
          !weekend && !schoolName ? 'bg-card' : null,
          holidayName ? 'border-red-500' : 'border-border',
          day.date === today ? 'ring-1 ring-primary' : null,
        )}
      />
    );
  }
  const primary = entries[0]!;
  return (
    <AbsenceChip
      entry={primary}
      extraMark={entries[1]?.mark ?? null}
      moreMarks={entries.length > 2}
      dayNumber={Number(day.date.slice(8, 10))}
      holidayName={holidayName}
      schoolName={schoolName}
      today={day.date === today}
      href={person.id === viewerId ? leaveRequestDetailPath(primary.requestId) : null}
    />
  );
}

function AbsenceChip({
  entry,
  extraMark = null,
  moreMarks = false,
  dayNumber,
  holidayName = null,
  schoolName = null,
  today = false,
  href,
}: {
  entry: LeaveCalendarEntry;
  extraMark?: string | null;
  moreMarks?: boolean;
  dayNumber: number;
  holidayName?: string | null;
  schoolName?: string | null;
  today?: boolean;
  href: string | null;
}) {
  const status =
    entry.status === 'approved' || entry.status === 'submitted' ? entry.status : 'approved';
  return (
    <LeaveDayCell
      size="chip"
      dayNumber={dayNumber}
      color={entry.color}
      mark={entry.mark}
      extraMark={extraMark}
      moreMarks={moreMarks}
      status={status}
      part={entry.part}
      holiday={Boolean(holidayName)}
      holidayName={holidayName}
      schoolHoliday={schoolName != null}
      schoolHolidayName={schoolName}
      today={today}
      labelDe={entry.labelDe}
      href={href}
    />
  );
}
